import React, {FC, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'
import {accountUsersAPI, IAccountMemberCreate, IAccountMemberUpdate} from '../../../services/AccountUsersService'
import {usersAPI} from '../../../services/UsersService'
import {useAccount} from '../../../hooks/useAccount'
import {IAccountUser, IAccountRole} from '../../../types/users/UserModel'
import {KTSVG} from '../../../template/helpers'
import {PageTitle} from '../../../template/layout/core'
import {Avatar} from '../../../template/helpers/components/Avatar'
import clsx from 'clsx'
import {InternalNumbersInput} from './components/InternalNumbersInput'

const modalsRoot = document.getElementById('root-modals') || document.body

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Владелец',
  SUPERVISOR: 'Супервайзер',
  AGENT: 'Агент',
}

const ROLE_BADGES: Record<string, string> = {
  OWNER: 'badge-light-danger',
  SUPERVISOR: 'badge-light-primary',
  AGENT: 'badge-light-warning',
}

const InviteStatusBadge: FC<{member: IAccountUser}> = ({member}) => {
  const content =
    member.invite_status === 'ACCEPTED' ? (
      <span className='badge badge-light-success fs-7'>Принято</span>
    ) : member.invite_status === 'SENT' ? (
      <span className='badge badge-light-warning fs-7'>Отправлено</span>
    ) : (
      <span className='text-muted fs-7'>—</span>
    )
  return <div className='d-flex align-items-center'>{content}</div>
}

type EditStateType = {
  id: string
  internalNumbers: string[]
  role: IAccountRole
}

type InviteRow = {
  key: string
  email: string
  internalNumbers: string[]
  role: IAccountRole
}

const NUMBERS_HINT =
  'Агенту доступны звонки этих номеров в журнале и отчётах: первым укажите свой номер, затем номера коллег. ' +
  'У владельца и супервайзера — только свой номер'

const numbersForRole = (role: IAccountRole | undefined, numbers: string[]) =>
  role === 'AGENT' ? numbers : numbers.slice(0, 1)

let _inviteKeySeq = 0
const newInviteRow = (): InviteRow => ({
  key: `invite-${++_inviteKeySeq}-${Date.now()}`,
  email: '',
  internalNumbers: [],
  role: 'AGENT',
})

const InternalNumbers: FC<{member: IAccountUser}> = ({member}) => {
  const numbers = member.internal_numbers ?? []
  if (!numbers.length) return <span className='text-muted'>—</span>
  return (
    <div className='d-flex flex-wrap gap-1'>
      {numbers.map((number, idx) =>
        idx === 0 && member.role === 'AGENT' ? (
          <span key={number} className='badge badge-light-primary fs-7' title='Свой номер агента'>
            {number}
          </span>
        ) : (
          <span key={number} className='badge badge-light fs-7 fw-semibold text-gray-700'>
            {number}
          </span>
        )
      )}
    </div>
  )
}

const ActionIconButton: FC<{
  variant: 'success' | 'danger' | 'primary'
  title: string
  iconPath: string
  onClick: () => void
  disabled?: boolean
}> = ({variant, title, iconPath, onClick, disabled}) => (
  <>
    <button
      type='button'
      title={title}
      className={`btn btn-icon btn-light-${variant} btn-active-color-light btn-sm me-1 align-self-center`}
      onClick={onClick}
      disabled={disabled}
    >
      <KTSVG path={iconPath} className='align-self-center' />
    </button>
  </>
)

const RoleSelect: FC<{
  value: IAccountRole
  onChange: (r: IAccountRole) => void
  disabled?: boolean
}> = ({value, onChange, disabled}) => (
  <select
    className='form-select form-select-sm form-select-solid d-inline-block text-center'
    style={{width: 'auto', verticalAlign: 'middle'}}
    value={value}
    onChange={(e) => onChange(e.target.value as IAccountRole)}
    disabled={disabled}
  >
    <option value='AGENT'>Агент</option>
    <option value='SUPERVISOR'>Супервайзер</option>
  </select>
)

export const AccountUsersPage: FC = () => {
  const intl = useIntl()
  const {currentAccountId, role: currentRole} = useAccount()
  const {data: currentUser} = usersAPI.useFetchGetMeQuery()
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<EditStateType | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [showInvite, setShowInvite] = useState(false)
  const [inviteRows, setInviteRows] = useState<InviteRow[]>([newInviteRow()])
  const [isInviting, setIsInviting] = useState(false)
  const [addUserId, setAddUserId] = useState('')
  const [addNumbers, setAddNumbers] = useState<string[]>([])
  const [addRole, setAddRole] = useState<IAccountRole>('AGENT')
  const [memberToDelete, setMemberToDelete] = useState<IAccountUser | null>(null)
  const [deleteEmail, setDeleteEmail] = useState('')
  const [buffer, setBuffer] = useState<string | null>(null)
  const [inviteNotice, setInviteNotice] = useState<{type: 'success' | 'danger'; text: string} | null>(null)

  const queryArg = currentAccountId ? {accountId: currentAccountId, skip: 0, limit: 100} : null

  const {data, isLoading} = accountUsersAPI.useFetchAccountUsersQuery(queryArg!, {skip: !queryArg})

  const [addAccountUser, {isLoading: isAdding}] = accountUsersAPI.useAddAccountUserMutation()
  const [updateAccountUser] = accountUsersAPI.useUpdateAccountUserMutation()
  const [deleteAccountUser] = accountUsersAPI.useDeleteAccountUserMutation()
  const [inviteUsers] = accountUsersAPI.useInviteUsersMutation()

  const members = useMemo(() => {
    const list = data?.data ?? []
    const term = search.toLowerCase()
    if (!term) return list
    return list.filter(
      (m) =>
        (m.email ?? '').toLowerCase().includes(term) ||
        (m.fullname ?? '').toLowerCase().includes(term) ||
        (m.internal_numbers ?? []).some((n) => n.toLowerCase().includes(term))
    )
  }, [data, search])

  const sortedMembers = [...members].sort((a, b) => Number(b.role === 'OWNER') - Number(a.role === 'OWNER'))

  const isOwner = currentRole === 'OWNER'
  const isSupervisor = currentRole === 'SUPERVISOR'
  const canManage = isOwner || isSupervisor

  const handleDelete = async (member: IAccountUser) => {
    if (member.id === currentUser?.id) return
    setMemberToDelete(member)
    setDeleteEmail('')
  }

  const closeDelete = () => {
    setMemberToDelete(null)
    setDeleteEmail('')
  }

  const confirmDelete = async () => {
    if (!memberToDelete) return
    if (deleteEmail.trim().toLowerCase() !== (memberToDelete.email ?? '').trim().toLowerCase()) return
    await deleteAccountUser({accountId: currentAccountId!, userId: memberToDelete.id})
    setEditing(null)
    closeDelete()
  }

  const handleSaveEdit = async (member: IAccountUser) => {
    if (!editing) return
    const body: IAccountMemberUpdate = {
      internal_numbers: numbersForRole(editing.role, editing.internalNumbers),
    }
    if (!(member.role === 'OWNER' || ((isOwner || isSupervisor) && member.id === currentUser?.id))) {
      body.role = editing.role
    }
    await updateAccountUser({accountId: currentAccountId!, userId: member.id, body})
    setEditing(null)
  }

  const handleSaveAdd = async () => {
    if (!addUserId) return
    const body: IAccountMemberCreate = {
      user_id: addUserId.trim(),
      role: addRole,
      internal_numbers: numbersForRole(addRole, addNumbers),
    }
    await addAccountUser({accountId: currentAccountId!, body})
    setShowAdd(false)
    setAddUserId('')
    setAddNumbers([])
    setAddRole('AGENT')
  }

  const closeAdd = () => {
    setShowAdd(false)
    setAddUserId('')
    setAddNumbers([])
    setAddRole('AGENT')
  }

  const closeInvite = () => {
    setShowInvite(false)
    setInviteRows([newInviteRow()])
    setIsInviting(false)
  }

  const openInvite = () => {
    setInviteRows([newInviteRow()])
    setIsInviting(false)
    setShowInvite(true)
  }

  const addInviteRow = () => setInviteRows((rows) => [...rows, newInviteRow()])

  const removeInviteRow = (key: string) => {
    setInviteRows((rows) => (rows.length > 1 ? rows.filter((r) => r.key !== key) : rows))
  }

  const updateInviteRow = (key: string, patch: Partial<InviteRow>) => {
    setInviteRows((rows) => rows.map((r) => (r.key === key ? {...r, ...patch} : r)))
  }

  const handleSaveInvite = async () => {
    const valid = inviteRows.filter((r) => r.email.trim())
    if (!valid.length) return
    setIsInviting(true)
    try {
      const result = await inviteUsers({
        accountId: currentAccountId!,
        body: {
          invites: valid.map((row) => ({
            email: row.email.trim(),
            role: row.role,
            internal_numbers: numbersForRole(row.role, row.internalNumbers),
          })),
        },
      }).unwrap()
      setInviteNotice({type: 'success', text: `Приглашений отправлено: ${result.sent} из ${result.total}`})
      closeInvite()
    } catch (e) {
      setInviteNotice({type: 'danger', text: 'Не удалось отправить приглашения'})
    } finally {
      setIsInviting(false)
    }
  }

  const startEdit = (member: IAccountUser) => {
    setEditing({
      id: member.id,
      internalNumbers: member.internal_numbers ?? [],
      role: member.role ?? 'AGENT',
    })
  }

  const isEditingRow = (member: IAccountUser) => editing !== null && editing.id === member.id

  const clipboard = (copyData: string) => {
    setBuffer(copyData)
    navigator.permissions.query({name: 'clipboard-write' as PermissionName}).then((result) => {
      if (result.state === 'granted' || result.state === 'prompt') {
        navigator.clipboard.writeText(copyData)
      }
    })
    setTimeout(() => {
      setBuffer(null)
    }, 600)
  }

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'ACCOUNT.USERS.PAGE_TITLE'})}</PageTitle>
      <div className='card'>
        <div className='card-header pt-4 pb-2'>
          <div className='d-flex flex-column justify-content-center'>
            <h3 className='text-gray-800 fw-bolder my-0'>Пользователи аккаунта</h3>
            <div className='text-muted fs-7'>Управление ролями и номерами сотрудников</div>
          </div>

          <div className='card-toolbar'>
            <div className='position-relative'>
              <span className='svg-icon svg-icon-2 position-absolute top-50 translate-middle-y ms-4'>
                <KTSVG path='/media/icons/duotune/general/gen021.svg' className='svg-icon-1' />
              </span>
              <input
                type='text'
                className='form-control form-control-solid ps-13 me-3'
                style={{width: '240px'}}
                placeholder='Поиск…'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {canManage && (
              <div className='d-flex gap-2'>
                <button className='btn btn-primary btn-sm' onClick={() => setShowAdd(true)}>
                  <i className='fas fa-plus me-1' /> Добавить
                </button>
                <button className='btn btn-primary btn-sm' onClick={openInvite}>
                  <i className='fas fa-solid fa-at me-1'></i> Пригласить
                </button>
              </div>
            )}
          </div>
        </div>

        <div className='card-body py-4'>
          {inviteNotice && (
            <div
              className={`alert alert-dismissible d-flex align-items-center p-4 mb-6 ${
                inviteNotice.type === 'success' ? 'alert-success' : 'alert-danger'
              }`}
            >
              <div className='d-flex flex-column'>
                <span>{inviteNotice.text}</span>
              </div>
              <button
                type='button'
                className='btn-close ms-auto'
                aria-label='Close'
                onClick={() => setInviteNotice(null)}
              ></button>
            </div>
          )}
          <div className='table-responsive'>
            <table className='table table-layout-fixed table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th style={{width: '300px'}}>Пользователь</th>
                  <th style={{width: '180px'}}>Email</th>
                  <th style={{width: '220px'}} title={NUMBERS_HINT}>
                    Номера
                  </th>
                  <th style={{width: '140px'}}>Роль</th>
                  <th style={{width: '140px'}}>Приглашение</th>
                  {canManage && (
                    <th className='text-end' style={{width: '130px'}}>
                      Действия
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={6} className='text-center py-10'>
                      <span className='spinner-border text-primary'></span>
                    </td>
                  </tr>
                )}

                {!isLoading && sortedMembers.length === 0 && (
                  <tr>
                    <td colSpan={6} className='text-center py-10 text-muted'>
                      Нет пользователей
                    </td>
                  </tr>
                )}

                {sortedMembers.map((member) =>
                  isEditingRow(member) ? (
                    <tr key={member.id}>
                      <td>
                        <div className='d-flex align-items-center overflow-hidden'>
                          <Avatar
                            className='symbol symbol-40px symbol-circle bg-light-primary me-3 flex-shrink-0'
                            userAvatar={member?.avatar}
                            userFullName={member?.fullname}
                          />
                          <div className='d-flex flex-column'>
                            <span className='text-gray-800 fw-bold text-truncate min-w-0'>
                              {member.fullname ?? '—'}
                            </span>

                            <div className='d-flex align-items-center'>
                              <div className='d-flex justify-content-start flex-column me-3'>
                                <span
                                  className={clsx(
                                    'badge',
                                    {'badge-light-info': buffer !== member.id},
                                    {'badge-light-success': buffer === member.id}
                                  )}
                                >
                                  {member.id.slice(0, -30)}...
                                </span>
                              </div>
                              <div className='d-flex justify-content-end flex-column'>
                                <button
                                  onClick={() => clipboard(member.id)}
                                  className={clsx(
                                    'btn btn-link text-gray-900 pt-0 pb-0',
                                    {'text-hover-gray-700': buffer !== member.id},
                                    {'text-hover-success': buffer === member.id}
                                  )}
                                >
                                  {buffer !== member.id ? (
                                    <KTSVG
                                      path='/media/icons/duotune/general/gen054.svg'
                                      className='svg-icon-primary svg-icon'
                                    />
                                  ) : (
                                    <KTSVG path='/media/icons/duotune/arrows/arr012.svg' className='svg-icon-success' />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className='text-muted text-nowrap text-truncate d-block'>{member.email ?? '—'}</span>
                      </td>
                      <td style={{width: '220px'}}>
                        <InternalNumbersInput
                          value={editing.internalNumbers}
                          onChange={(numbers) => setEditing((s) => ({...s!, internalNumbers: numbers}))}
                          isAgent={editing.role === 'AGENT'}
                        />
                      </td>
                      <td style={{width: '140px'}}>
                        {member.role === 'OWNER' || (member.id === currentUser?.id && (isOwner || isSupervisor)) ? (
                          <span className={`badge ${ROLE_BADGES[editing.role] ?? 'badge-light'} fs-7`}>
                            {ROLE_LABELS[editing.role] ?? editing.role}
                          </span>
                        ) : (
                          <RoleSelect value={editing.role} onChange={(r) => setEditing((s) => ({...s!, role: r}))} />
                        )}
                      </td>
                      <td>
                        <InviteStatusBadge member={member} />
                      </td>
                      <td className='text-end'>
                        <ActionIconButton
                          variant='success'
                          title='Сохранить'
                          iconPath='/media/icons/duotune/general/gen037.svg'
                          onClick={() => handleSaveEdit(member)}
                        />
                        {member.id !== currentUser?.id && member.role !== 'OWNER' && (
                          <ActionIconButton
                            variant='danger'
                            title='Удалить'
                            iconPath='/media/icons/duotune/general/gen027.svg'
                            onClick={() => handleDelete(member)}
                          />
                        )}
                        <ActionIconButton
                          variant='primary'
                          title='Отмена'
                          iconPath='/media/icons/duotune/general/gen034.svg'
                          onClick={() => setEditing(null)}
                        />
                      </td>
                    </tr>
                  ) : (
                    <tr key={member.id}>
                      <td>
                        <div className='d-flex align-items-center overflow-hidden'>
                          <Avatar
                            className='symbol symbol-40px symbol-circle bg-light-primary me-3 flex-shrink-0'
                            userAvatar={member?.avatar}
                            userFullName={member?.fullname}
                          />
                          <div className='d-flex flex-column'>
                            <span className='text-gray-800 fw-bold text-truncate min-w-0'>
                              {member.fullname ?? '—'}
                            </span>

                            <div className='d-flex align-items-center'>
                              <div className='d-flex justify-content-start flex-column me-3'>
                                <span
                                  className={clsx(
                                    'badge',
                                    {'badge-light-info': buffer !== member.id},
                                    {'badge-light-success': buffer === member.id}
                                  )}
                                >
                                  {member.id.slice(0, -30)}...
                                </span>
                              </div>
                              <div className='d-flex justify-content-end flex-column'>
                                <button
                                  onClick={() => clipboard(member.id)}
                                  className={clsx(
                                    'btn btn-link text-gray-900 pt-0 pb-0',
                                    {'text-hover-gray-700': buffer !== member.id},
                                    {'text-hover-success': buffer === member.id}
                                  )}
                                >
                                  {buffer !== member.id ? (
                                    <KTSVG
                                      path='/media/icons/duotune/general/gen054.svg'
                                      className='svg-icon-primary svg-icon'
                                    />
                                  ) : (
                                    <KTSVG path='/media/icons/duotune/arrows/arr012.svg' className='svg-icon-success' />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className='text-muted text-nowrap text-truncate d-block'>{member.email ?? '—'}</span>
                      </td>
                      <td className='ps-5' style={{width: '220px'}}>
                        <InternalNumbers member={member} />
                      </td>
                      <td style={{width: '140px'}}>
                        {member.role ? (
                          <span className={`badge ${ROLE_BADGES[member.role] ?? 'badge-light'} fs-7`}>
                            {ROLE_LABELS[member.role] ?? member.role}
                          </span>
                        ) : (
                          <span className='text-muted fs-7'>—</span>
                        )}
                      </td>
                      <td>
                        <InviteStatusBadge member={member} />
                      </td>
                      {canManage && (
                        <td className='text-end'>
                          {member.role && (
                            <ActionIconButton
                              variant='primary'
                              title='Редактировать'
                              iconPath='/media/icons/duotune/art/art005.svg'
                              onClick={() => startEdit(member)}
                            />
                          )}
                          {member.invite_status === 'SENT' && (
                            <ActionIconButton
                              variant='danger'
                              title='Удалить приглашение'
                              iconPath='/media/icons/duotune/general/gen027.svg'
                              onClick={() => handleDelete(member)}
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {createPortal(
        <Modal show={showAdd} onHide={closeAdd} centered>
          <Modal.Header closeButton>
            <Modal.Title>Добавить пользователя</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='fv-row mb-7'>
              <label className='required form-label fw-bold fs-6 mb-2'>Пользователь</label>
              <input
                type='text'
                className='form-control form-control-solid'
                placeholder='UUID пользователя'
                value={addUserId}
                onChange={(e) => setAddUserId(e.target.value)}
              />
              <div className='text-muted mt-1 fs-7'>Введите идентификатор (UUID) зарегистрированного пользователя</div>
            </div>
            <div className='fv-row mb-7'>
              <label className='form-label fw-bold fs-6 mb-2'>Номера</label>
              <InternalNumbersInput value={addNumbers} onChange={setAddNumbers} isAgent={addRole === 'AGENT'} />
              <div className='text-muted mt-1 fs-7'>
                {addRole === 'AGENT'
                  ? 'Звонки этих номеров доступны агенту в журнале и отчётах: первым укажите свой номер, затем номера коллег. Номер добавляется по Enter, пробелу или запятой'
                  : 'Свой внутренний номер'}
              </div>
            </div>
            <div className='fv-row mb-7'>
              <label className='form-label fw-bold fs-6 mb-2'>Роль</label>
              <select
                className='form-select form-select-solid'
                value={addRole}
                onChange={(e) => setAddRole(e.target.value as IAccountRole)}
              >
                <option value='AGENT'>Агент</option>
                <option value='SUPERVISOR'>Супервайзер</option>
              </select>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type='button' className='btn btn-light' onClick={closeAdd}>
              Отмена
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleSaveAdd}
              disabled={!addUserId.trim() || isAdding}
            >
              {isAdding && <span className='spinner-border spinner-border-sm me-2'></span>}
              Сохранить
            </button>
          </Modal.Footer>
        </Modal>,
        modalsRoot
      )}

      {createPortal(
        <Modal show={!!memberToDelete} onHide={closeDelete} centered>
          <Modal.Header closeButton>
            <Modal.Title>Удалить пользователя</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='text-gray-800 mb-3'>
              Удалить <span className='fw-bold'>{memberToDelete?.fullname ?? memberToDelete?.email}</span> из аккаунта?
            </div>
            <div className='fs-7 text-muted mb-4'>Для подтверждения введите email пользователя</div>
            <input
              type='text'
              className='form-control form-control-solid'
              placeholder='email пользователя'
              value={deleteEmail}
              onChange={(e) => setDeleteEmail(e.target.value)}
            />
          </Modal.Body>
          <Modal.Footer>
            <button type='button' className='btn btn-light' onClick={closeDelete}>
              Отмена
            </button>
            <button
              type='button'
              className='btn btn-danger'
              onClick={confirmDelete}
              disabled={
                !memberToDelete ||
                deleteEmail.trim().toLowerCase() !== (memberToDelete.email ?? '').trim().toLowerCase()
              }
            >
              Удалить
            </button>
          </Modal.Footer>
        </Modal>,
        modalsRoot
      )}

      {createPortal(
        <Modal show={showInvite} onHide={closeInvite} centered size='lg'>
          <Modal.Header closeButton>
            <Modal.Title>
              Пригласить пользователей
              <div className='text-muted fs-7'>Укажите email зарегистрированного пользователя</div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='fw-bold text-muted fs-7 d-flex align-items-center mb-2'>
              <span className='me-2' style={{width: '24px'}}></span>
              <span className='flex-grow-1 required'>Email</span>
              <span className='text-center' style={{width: '220px'}} title={NUMBERS_HINT}>
                Номера
              </span>
              <span className='text-center' style={{width: '170px'}}>
                Роль
              </span>
            </div>
            {inviteRows.map((row, idx) => (
              <div key={row.key} className='d-flex align-items-center gap-2 mb-3'>
                <span className='text-gray-500 fw-bold my-0' style={{width: '24px'}}>
                  {idx + 1}.
                </span>
                <input
                  type='text'
                  className='form-control form-control-solid flex-grow-1'
                  placeholder='Введите email'
                  value={row.email}
                  onChange={(e) => updateInviteRow(row.key, {email: e.target.value})}
                />
                <div style={{width: '220px'}} className='flex-shrink-0'>
                  <InternalNumbersInput
                    value={row.internalNumbers}
                    onChange={(numbers) => updateInviteRow(row.key, {internalNumbers: numbers})}
                    isAgent={row.role === 'AGENT'}
                  />
                </div>
                <select
                  className='form-select form-select-solid text-center'
                  style={{width: '170px'}}
                  value={row.role}
                  onChange={(e) => updateInviteRow(row.key, {role: e.target.value as IAccountRole})}
                >
                  <option value='AGENT'>Агент</option>
                  <option value='SUPERVISOR'>Супервайзер</option>
                </select>
                {inviteRows.length > 1 && (
                  <button
                    type='button'
                    className='btn btn-icon btn-light-danger btn-sm flex-shrink-0'
                    title='Удалить строку'
                    onClick={() => removeInviteRow(row.key)}
                  >
                    <KTSVG path='/media/icons/duotune/general/gen027.svg' />
                  </button>
                )}
              </div>
            ))}
            <button type='button' className='btn btn-light-primary btn-sm' onClick={addInviteRow}>
              <i className='fas fa-plus me-1' /> Добавить ещё
            </button>
          </Modal.Body>
          <Modal.Footer>
            <button type='button' className='btn btn-light btn-sm' onClick={closeInvite}>
              Отмена
            </button>
            <button
              type='button'
              className='btn btn-primary btn-sm'
              onClick={handleSaveInvite}
              disabled={isInviting || !inviteRows.some((r) => r.email.trim())}
            >
              {isInviting && <span className='spinner-border spinner-border-sm me-2'></span>}
              Пригласить
            </button>
          </Modal.Footer>
        </Modal>,
        modalsRoot
      )}
    </>
  )
}
